"use client"

import { createContext, useContext, useState, type ReactNode } from "react"
import { type Notification } from "./notification-panel"

type NotificationType = "success" | "error" | "info"

interface NotificationContextType {
  addNotification: (
    title: string,
    description?: string,
    type?: NotificationType,
    link?: { url: string; text: string; isExternal?: boolean },
  ) => void
  dismissNotification: (id: string) => void
  notifications: Notification[]
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([])

  const addNotification = (
    title: string,
    description?: string,
    type: NotificationType = "info",
    link?: { url: string; text: string; isExternal?: boolean },
  ) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newNotification: Notification = {
      id,
      title,
      description,
      type,
      timestamp: new Date(),
      link,
    }

    setNotifications((prev) => [...prev, newNotification])
  }

  const dismissNotification = (id: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id))
  }

  return (
    <NotificationContext.Provider value={{ addNotification, dismissNotification, notifications }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationProvider")
  }
  return context
}
